import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Chip, Skeleton, fmtCoins } from "../components/ui";
import { BannerChars } from "../components/lux";
import ChatIcon from "../components/chat/ChatIcon";
import CoinHistory from "../components/coins/CoinHistory";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../services/api";
import { errorInfo } from "../services/errors";
import "../styles/chat.css";
import "../styles/coins.css";

const FAQ = [
  ["คุยพ้อยท์ได้จากไหน", "ได้จากการเติมคอยน์ ประมาณ 1 พ้อยท์ต่อบาท (ตามที่ระบุในแต่ละแพ็กเกจ)"],
  ["ระดับคำนวณอย่างไร", "ใช้ค่าที่สูงกว่าระหว่างพ้อยท์เดือนนี้และเดือนก่อน เมื่อขึ้นเดือนใหม่พ้อยท์เดือนนี้จะรีเซ็ตเป็น 0 แต่ยังนับยอดเดือนก่อนต่ออีกหนึ่งเดือน"],
  ["คอยน์รายเดือนรับอย่างไร", "ระดับ Master ขึ้นไปรับคอยน์ฟรีได้เดือนละครั้งต่อระดับ ระดับที่สูงกว่ารับของระดับที่ต่ำกว่าได้ด้วย กดปุ่ม \"รับคอยน์รายเดือน\" ในหน้านี้"],
  ["โน้ตตัวละครได้กี่ตัวอักษร", "ขึ้นกับระดับของคุณ ยิ่งระดับสูงยิ่งเขียนโน้ตให้ AI จำได้ยาวขึ้น"],
];

const monthEnd = () => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString("th-TH", { day: "numeric", month: "long", year: "numeric" });

function Gauge({ pct, tier, points }) {
  return (
    <div className="gauge" role="img" aria-label={`ระดับ ${tier} ${points} คุยพ้อยท์ ความคืบหน้า ${Math.round(pct)} เปอร์เซ็นต์`}>
      <svg viewBox="0 0 200 110" aria-hidden="true">
        <defs><linearGradient id="gauge-g" x1="0" x2="1"><stop offset="0%" stopColor="#8b5cf6" /><stop offset="100%" stopColor="#ec4899" /></linearGradient></defs>
        <path d="M10 100 A90 90 0 0 1 190 100" fill="none" stroke="var(--surface-3)" strokeWidth="12" strokeLinecap="round" />
        <path d="M10 100 A90 90 0 0 1 190 100" fill="none" stroke="url(#gauge-g)" strokeWidth="12" strokeLinecap="round" pathLength="100" strokeDasharray={`${Math.max(pct, pct > 0 ? 1.5 : 0)} 100`} />
      </svg>
      <div className="gauge__center"><span className="gauge__gem" aria-hidden="true">💎</span><span className="gauge__tier">{tier}</span><span className="muted" style={{ fontSize: 13 }}>{fmtCoins(points)} คุยพ้อยท์</span></div>
    </div>
  );
}

export default function Club() {
  const { setCoins } = useAuth();
  const toast = useToast();
  const vip = useAsync(() => api.coins.club(), []);
  const [claiming, setClaiming] = useState(false);
  const [hv, setHv] = useState(0);
  const s = vip.data;

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await api.coins.claimMonthly();
      if (typeof r.tokensLeft === "number") setCoins(r.tokensLeft);
      toast(`รับคอยน์รายเดือน +${fmtCoins(r.coins)} คอยน์แล้ว`, "success");
      setHv((v) => v + 1);
      vip.reload();
    } catch (e) {
      toast(errorInfo(e).message, "error");
    } finally {
      setClaiming(false);
    }
  };

  const next = s?.nextTier;
  const target = next?.minPoints || 0;
  const pct = !s ? 0 : next ? Math.min(100, (s.effectivePoints / target) * 100) : 100;

  return (
    <div className="club-page">
      <div className="page-head page-head--art"><div className="grow"><h1>คุยคลับ</h1><p>เติมคอยน์เพื่อสะสมคุยพ้อยท์ ปลดสิทธิพิเศษตามระดับ (พ้อยท์รีเซ็ตต้นเดือน)</p></div><BannerChars ids={["373", "14", "432"]} /></div>

      {vip.error ? (
        <div className="notice notice--error" role="alert"><span className="grow">{vip.error}</span><Button size="sm" onClick={vip.reload}>ลองใหม่</Button></div>
      ) : !s ? <Skeleton height={260} radius="16px" /> : (
        <>
          <Card grad className="club-hero">
            <Gauge pct={pct} tier={s.tier.name} points={s.currentMonthPoints} />
            {next ? (
              <>
                <h3 className="mt-16">เก็บอีก <span style={{ color: "var(--success)" }}>{fmtCoins(Math.max(0, target - s.effectivePoints))} คุยพ้อยท์</span> เพื่อเลื่อนขั้นสู่ {next.name}</h3>
                <p className="muted mt-8" style={{ fontSize: 13.5 }}>สะสมให้ครบภายใน {monthEnd()} · ถึง {fmtCoins(target)} พ้อยท์ ({fmtCoins(s.effectivePoints)}/{fmtCoins(target)})</p>
              </>
            ) : <h3 className="mt-16">คุณอยู่ระดับสูงสุดแล้ว</h3>}
            <div className="stats mt-16" style={{ gridTemplateColumns: "1fr 1fr", maxWidth: 360, margin: "16px auto 0" }}>
              <div className="stat"><b>{fmtCoins(s.currentMonthPoints)}</b><span>พ้อยท์เดือนนี้</span></div>
              <div className="stat"><b>{fmtCoins(s.previousMonthPoints)}</b><span>พ้อยท์เดือนก่อน</span></div>
            </div>
            <p className="faint mt-8" style={{ fontSize: 12 }}>คุยพ้อยท์จะถูกรีเซ็ตเป็น 0 เมื่อขึ้นเดือนใหม่ ระดับคำนวณจากยอดที่สูงกว่าของสองเดือน</p>
            <div className="row mt-16" style={{ justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/coins" className="btn btn--primary btn--lg">ซื้อคอยน์เพิ่ม</Link>
              {s.canClaimMonthly ? <Button size="lg" onClick={claim} disabled={claiming}>{claiming ? "กำลังรับ..." : `รับคอยน์รายเดือน +${fmtCoins(s.monthlyCoins)}`}</Button> : null}
            </div>
          </Card>

          <h2 className="mt-24 mb-8">สิทธิประโยชน์ตามระดับ</h2>
          <div className="tier-ladder">
            {s.tiers.map((t, i) => {
              const reached = i <= s.tierIndex;
              return (
                <div key={t.id || t.name} className={`tier${i === s.tierIndex ? " is-current" : ""}${reached ? "" : " is-locked"}`}>
                  <div className="tier__head">
                    <span aria-hidden="true">{reached ? "💎" : ""}</span>{!reached ? <ChatIcon name="lock" size={16} /> : null}
                    <span className="tier__name">{t.name}</span>
                    {i === s.tierIndex ? <Chip size="sm" className="is-active">ระดับของคุณ</Chip> : null}
                    <span className="grow" />
                    <span className="faint" style={{ fontSize: 12 }}>{t.minPoints ? `ตั้งแต่ ${fmtCoins(t.minPoints)} พ้อยท์` : "เริ่มต้น"}</span>
                  </div>
                  <div className="tier__tags">
                    <Chip size="sm">โน้ต {fmtCoins(t.noteLimit)} ตัวอักษร</Chip>
                    <Chip size="sm">เช็คอิน x{t.checkinMultiplier}</Chip>
                    {t.monthlyCoins ? <Chip size="sm" className="is-active">คอยน์ฟรี +{fmtCoins(t.monthlyCoins)}/เดือน</Chip> : null}
                  </div>
                  {t.perks?.length ? <ul className="tier__perks">{t.perks.map((p) => <li key={p}>{p}</li>)}</ul> : null}
                </div>
              );
            })}
          </div>
          <p className="faint mt-8" style={{ fontSize: 12.5 }}>ระดับที่สูงกว่ารับคอยน์รายเดือนของระดับที่ต่ำกว่าได้ด้วย (เดือนละครั้งต่อระดับ)</p>
        </>
      )}

      <h2 className="mt-24 mb-8">ประวัติการทำรายการล่าสุด</h2>
      <Card><CoinHistory compact refreshKey={hv} /></Card>

      <h2 className="mt-24 mb-8">คำถามที่พบบ่อย</h2>
      <Card className="faq">{FAQ.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}</Card>
    </div>
  );
}
